import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { getRBACAuditLogs } from "../services/rbacservice";
import { FaHistory, FaSearch, FaFilter, FaUserShield, FaCalendarAlt, FaLaptopCode, FaSyncAlt } from "react-icons/fa";
import Pagination from "../common/pagination";
import Dropdown from "../modal/dropdown";
import "./role_management.css";

export default function AuditLogsModal({ onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [targetTypeFilter, setTargetTypeFilter] = useState("all");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getRBACAuditLogs();
      setLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error("Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const filteredLogs = logs.filter((log) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      (log.target_description && log.target_description.toLowerCase().includes(q)) ||
      (log.actor_email && log.actor_email.toLowerCase().includes(q)) ||
      (log.action && log.action.toLowerCase().includes(q)) ||
      (log.ip_address && log.ip_address.toLowerCase().includes(q));

    const matchAction = actionFilter === "all" || log.action === actionFilter;
    const matchTarget = targetTypeFilter === "all" || log.target_type === targetTypeFilter;

    return matchSearch && matchAction && matchTarget;
  });

  const totalPages = Math.max(1, Math.ceil(filteredLogs.length / PAGE_SIZE));
  const paginatedLogs = filteredLogs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const formatActionBadge = (action) => {
    const badgeStyle = {
      padding: "4px 10px",
      borderRadius: "12px",
      fontSize: "0.75rem",
      fontWeight: "700",
      textTransform: "uppercase",
      display: "inline-block",
    };

    if (action?.includes("created") || action?.includes("assigned") || action?.includes("granted")) {
      return <span style={{ ...badgeStyle, background: "#d1fae5", color: "#065f46" }}>{action.replace(/_/g, " ")}</span>;
    }
    if (action?.includes("deleted") || action?.includes("removed") || action?.includes("denied")) {
      return <span style={{ ...badgeStyle, background: "#fee2e2", color: "#991b1b" }}>{action.replace(/_/g, " ")}</span>;
    }
    return <span style={{ ...badgeStyle, background: "#e0e7ff", color: "#3730a3" }}>{action?.replace(/_/g, " ") || "action"}</span>;
  };

  return (
    <div className="rbac-modal-overlay">
      <div className="rbac-modal" style={{ maxWidth: "900px", width: "95%", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <div className="rbac-modal-header" style={{ borderBottom: "1px solid #e2e8f0", paddingBottom: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ background: "#eff6ff", color: "#2563eb", padding: "10px", borderRadius: "10px", display: "flex" }}>
              <FaHistory size={20} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: "1.3rem", color: "#1e293b" }}>Security & RBAC Audit Logs</h2>
              <span style={{ fontSize: "0.8rem", color: "#64748b" }}>
                Comprehensive real-time audit trail of all role changes, permissions, and access modifications
              </span>
            </div>
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <button className="btn-secondary" onClick={loadLogs} title="Refresh Logs" style={{ padding: "6px 10px" }}>
              <FaSyncAlt />
            </button>
            <button className="btn-secondary" onClick={onClose} style={{ padding: "6px 12px", fontWeight: "bold" }}>✕</button>
          </div>
        </div>

        {/* Filters and Search Bar */}
        <div style={{ padding: "16px 24px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ flex: "1 1 250px", position: "relative" }}>
            <FaSearch style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
            <input
              type="text"
              placeholder="Search by actor, description, IP address..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              style={{ width: "100%", padding: "8px 12px 8px 36px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "0.85rem", background: "#ffffff" }}
            />
          </div>

          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <FaFilter color="#64748b" />
            <div className="rbac-audit-dropdown">
              <Dropdown
                options={[
                  { label: "All Actions", value: "all" },
                  { label: "Role Created", value: "role_created" },
                  { label: "Role Updated", value: "role_updated" },
                  { label: "Role Deleted", value: "role_deleted" },
                  { label: "Role Cloned", value: "role_cloned" },
                  { label: "Role Toggled", value: "role_toggled" },
                  { label: "User Role Assigned", value: "user_role_assigned" },
                  { label: "User Role Removed", value: "user_role_removed" },
                ]}
                value={actionFilter}
                onChange={(val) => { setActionFilter(val); setPage(1); }}
                placeholder="All Actions"
              />
            </div>

            <div className="rbac-audit-dropdown">
              <Dropdown
                options={[
                  { label: "All Targets", value: "all" },
                  { label: "Role", value: "Role" },
                  { label: "User Role", value: "UserRole" },
                  { label: "Permission", value: "Permission" },
                  { label: "Override", value: "UserPermissionOverride" },
                ]}
                value={targetTypeFilter}
                onChange={(val) => { setTargetTypeFilter(val); setPage(1); }}
                placeholder="All Targets"
              />
            </div>
          </div>
        </div>

        {/* Audit Logs Table */}
        <div className="rbac-modal-body" style={{ flex: 1, overflowY: "auto", padding: "0" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: "60px", color: "#64748b" }}>
              <FaSyncAlt className="spinning" style={{ fontSize: "2rem", marginBottom: "12px", color: "#3b82f6" }} />
              <p>Loading security audit logs...</p>
            </div>
          ) : paginatedLogs.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px", color: "#64748b" }}>
              <FaHistory style={{ fontSize: "2.5rem", marginBottom: "12px", color: "#cbd5e1" }} />
              <p style={{ fontWeight: 600, color: "#475569" }}>No security audit logs found</p>
              <span style={{ fontSize: "0.85rem" }}>Any role adjustments, user assignments, or permissions changes will appear here automatically.</span>
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: "0.85rem" }}>
              <thead>
                <tr style={{ background: "#f1f5f9", borderBottom: "2px solid #e2e8f0", color: "#475569" }}>
                  <th style={{ padding: "12px 16px" }}>Action</th>
                  <th style={{ padding: "12px 16px" }}>Actor & IP</th>
                  <th style={{ padding: "12px 16px" }}>Description</th>
                  <th style={{ padding: "12px 16px" }}>Changes / Details</th>
                  <th style={{ padding: "12px 16px" }}>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {paginatedLogs.map((log) => (
                  <tr key={log.id} style={{ borderBottom: "1px solid #f1f5f9", transition: "background 0.2s" }} className="audit-row">
                    <td style={{ padding: "14px 16px", verticalAlign: "top" }}>
                      {formatActionBadge(log.action)}
                    </td>
                    <td style={{ padding: "14px 16px", verticalAlign: "top" }}>
                      <div style={{ fontWeight: 600, color: "#1e293b", display: "flex", alignItems: "center", gap: "6px" }}>
                        <FaUserShield color="#3b82f6" size={12} />
                        {log.actor_email || "System"}
                      </div>
                      {log.ip_address && (
                        <div style={{ fontSize: "0.75rem", color: "#64748b", marginTop: "4px", display: "flex", alignItems: "center", gap: "4px" }}>
                          <FaLaptopCode size={10} /> {log.ip_address}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "14px 16px", verticalAlign: "top" }}>
                      <span style={{ fontWeight: 600, color: "#0f172a" }}>{log.target_description || "—"}</span>
                      {log.target_type && (
                        <span style={{ display: "block", fontSize: "0.75rem", color: "#64748b", marginTop: "2px" }}>
                          Target: {log.target_type} #{log.target_id || ""}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "14px 16px", verticalAlign: "top", maxWidth: "250px" }}>
                      {log.old_value || log.new_value ? (
                        <div style={{ fontSize: "0.75rem", background: "#f8fafc", border: "1px solid #e2e8f0", padding: "6px 8px", borderRadius: "6px", fontFamily: "monospace" }}>
                          {log.old_value && (
                            <div style={{ color: "#991b1b", marginBottom: "2px" }}>
                              <strong>Old:</strong> {JSON.stringify(log.old_value)}
                            </div>
                          )}
                          {log.new_value && (
                            <div style={{ color: "#065f46" }}>
                              <strong>New:</strong> {JSON.stringify(log.new_value)}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: "#94a3b8" }}>No payload recorded</span>
                      )}
                    </td>
                    <td style={{ padding: "14px 16px", verticalAlign: "top", whiteSpace: "nowrap", color: "#475569", fontSize: "0.8rem" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <FaCalendarAlt size={11} color="#94a3b8" />
                        {log.created_at ? new Date(log.created_at).toLocaleString() : "—"}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination Footer */}
        <div style={{ padding: "12px 24px", background: "#f8fafc", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: "0.85rem", color: "#64748b" }}>
            Showing <strong>{(page - 1) * PAGE_SIZE + 1}</strong> to <strong>{Math.min(page * PAGE_SIZE, filteredLogs.length)}</strong> of <strong>{filteredLogs.length}</strong> logs
          </span>
          <Pagination page={page} totalPages={totalPages} setPage={setPage} style={{ marginTop: 0 }} />
        </div>
      </div>
    </div>
  );
}
